<script lang="ts">
  import Modal from '@shared/ui/Modal.svelte';
  import { formatBarAssociationLabel } from '@shared/constants/choices';
  import type { Lawyer } from '@domain/lawyer/model';

  /**
   * 代表弁護士 (0..N) の複数選択モーダル
   * (spec 20260612-multiple-firm-representative-lawyers)。
   *
   * - 候補はその firm の全所属弁護士 (未削除。 firmLawyersStore が全ページ取得)。
   * - チェックボックスリストで複数選択。全解除 = 代表 0 名も正常に保存できる。
   * - 防御: 候補リストに現在の代表が含まれない場合 (取得失敗・打ち切り等) でも
   *   `current` (firm detail の embed 由来) から行を補い、選択済みとして必ず表示する。
   *   初期チェックは現代表全員 — 候補に無い代表を黙って外さない。
   * - 保存は親 (FirmDetailPage) が専用 API (PUT /representatives) を呼ぶ。
   */
  interface RepresentativeRow {
    id: string;
    full_name: string;
    bar_association: string | null;
  }

  interface Props {
    open: boolean;
    /// Lawyers attached to the firm (candidates).
    candidates: Lawyer[];
    /// 現在の代表 (firm detail の `representative_lawyers` embed 由来)。
    current: RepresentativeRow[];
    onClose: () => void;
    /// 選択した lawyer id 集合で全置換する。成功時は親が再取得して閉じる。
    onSubmit: (lawyerIds: string[]) => void | Promise<void>;
  }

  let { open, candidates, current, onClose, onSubmit }: Props = $props();

  /// 表示行 = 候補 (一覧順) + 候補に含まれない現代表 (末尾に補完)。
  const rows = $derived.by(() => {
    const map = new Map<string, RepresentativeRow>();
    for (const lawyer of candidates) {
      map.set(lawyer.id, {
        id: lawyer.id,
        full_name: lawyer.full_name,
        bar_association: lawyer.bar_association,
      });
    }
    for (const rep of current) {
      if (!map.has(rep.id)) map.set(rep.id, rep);
    }
    return [...map.values()];
  });

  let selected = $state<string[]>([]);
  let submitting = $state(false);
  let serverError = $state<string | null>(null);

  $effect(() => {
    if (open) {
      // 現代表全員を初期チェック (候補リスト外の代表もフィルタしない)。
      selected = current.map((rep) => rep.id);
      serverError = null;
    }
  });

  function toggle(id: string) {
    selected = selected.includes(id) ? selected.filter((v) => v !== id) : [...selected, id];
  }

  function close() {
    if (submitting) return;
    serverError = null;
    onClose();
  }

  async function handleSubmit(ev: SubmitEvent) {
    ev.preventDefault();
    if (submitting) return;
    submitting = true;
    serverError = null;
    try {
      await onSubmit(selected);
    } catch (err) {
      serverError = err instanceof Error ? err.message : String(err);
    } finally {
      submitting = false;
    }
  }
</script>

<Modal {open} title="代表弁護士を変更" onClose={close}>
  <form id="set-reps-form" onsubmit={handleSubmit}>
    {#if rows.length === 0}
      <p class="muted">この事務所には弁護士が登録されていません。</p>
    {:else}
      <p class="hint">代表に設定する弁護士を選択してください (複数可・全解除可)。</p>
      <ul class="candidate-list" data-testid="set-representatives-list">
        {#each rows as row (row.id)}
          <li>
            <label class="candidate">
              <input
                type="checkbox"
                checked={selected.includes(row.id)}
                disabled={submitting}
                onchange={() => toggle(row.id)}
              />
              <span>
                {row.full_name.trim()}
                {#if row.bar_association}
                  <span class="muted small"
                    >（{formatBarAssociationLabel(row.bar_association)}）</span
                  >
                {/if}
              </span>
            </label>
          </li>
        {/each}
      </ul>
      {#if selected.length === 0}
        <p class="muted small">代表弁護士なし (未設定) として保存されます。</p>
      {/if}
    {/if}
    {#if serverError}<p class="server-error">{serverError}</p>{/if}
  </form>
  {#snippet footer()}
    <button type="button" onclick={close} disabled={submitting}>キャンセル</button>
    <button
      type="submit"
      form="set-reps-form"
      class="primary"
      disabled={submitting || rows.length === 0}
    >
      {submitting ? '保存中…' : '保存'}
    </button>
  {/snippet}
</Modal>

<style>
  form {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .hint {
    margin: 0;
    font-size: 0.875rem;
    color: #374151;
  }
  .candidate-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-height: 320px;
    overflow-y: auto;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
  }
  .candidate {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    cursor: pointer;
  }
  .candidate:hover {
    background: #f9fafb;
  }
  .candidate input[type='checkbox'] {
    cursor: pointer;
  }
  .muted {
    color: #6b7280;
  }
  .small {
    font-size: 0.75rem;
  }
  .server-error {
    color: #b91c1c;
    margin: 0;
  }
  button {
    padding: 8px 16px;
    border: 1px solid #d1d5db;
    background: white;
    border-radius: 4px;
    cursor: pointer;
    font: inherit;
  }
  button.primary {
    background: #2563eb;
    border-color: #2563eb;
    color: white;
  }
  button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
</style>
